import type {
	IAnchorScrollProvider,
	ICacheFactoryService,
	ICompileService,
	IControllerService,
	IDocumentService,
	IExceptionHandlerService,
	IFilterService,
	IHttpBackendService,
	IHttpParamSerializer,
	IHttpService,
	IInterpolateService,
	IIntervalService,
	ILocaleService,
	ILocationService,
	ILogService,
	IParseService,
	IQService,
	IRootElementService,
	IRootScopeService,
	ISCEDelegateService,
	ISCEService,
	ITemplateCacheService,
	ITemplateRequestService,
	ITimeoutService,
	IWindowService,
} from "angular";
import type { ALKMaps, LonLat } from "./alk-maps";

export function inject<K extends keyof InjectorMap>(name: K): InjectorMap[K] {
	return window.angular.element(document).injector().get(name);
}

export type InjectorMap = {
	$anchorScroll: IAnchorScrollProvider;
	$cacheFactory: ICacheFactoryService;
	$compile: ICompileService;
	$controller: IControllerService;
	$document: IDocumentService;
	$exceptionHandler: IExceptionHandlerService;
	$filter: IFilterService;
	$http: IHttpService;
	$httpBackend: IHttpBackendService;
	$httpParamSerializer: IHttpParamSerializer;
	$httpParamSerializerJQLike: IHttpParamSerializer;
	$interpolate: IInterpolateService;
	$interval: IIntervalService;
	$locale: ILocaleService;
	$location: ILocationService;
	$log: ILogService;
	$parse: IParseService;
	$q: IQService;
	$rootElement: IRootElementService;
	$rootScope: IRootScopeService;
	$sce: ISCEService;
	$sceDelegate: ISCEDelegateService;
	$templateCache: ITemplateCacheService;
	$templateRequest: ITemplateRequestService;
	$timeout: ITimeoutService;
	$window: IWindowService;
} & {
	UserProvider: UserProvider;
	mapService: MapService;
	mapUtilities: MapUtilities;
	alkGeocodeService: AlkGeocodeService;
	STATES: ReadonlyArray<State>;
};

export type UserProvider = {
	getUser(): User;
	loadUser(): void;
	clearUser(): void;
};

export type User = {
	isLoggedIn: boolean;
	isALKUser: boolean;
	dataVersion: string;
	region: string;
	satelliteProvider: string;
	gridDataVersion: string;
	availableRegions: string[];
	isUploadGeoJSONEnabled: boolean;
	isLinkSplitEnabled: boolean;
	userName: string;
	firstName: string;
	lastName: string;
	account: string;
	userId: number;
	accountId: number;
	expired: boolean;
	expirationDate: string | null;
	isDisabled: false;
	roles: string[];
	hasBillingIssue: false;
	authToken: string;
	currentTermsOfUseAccepted: boolean;
	memberSince: string;
	supportAccount: string;
	groups: UserGroup[];
};

export type UserGroup = {
	AccountId: number;
	CreatedBy: string;
	Description: string;
	Id: number;
	ModifiedBy: string;
	Name: string;
};

export type MapService = {
	getMap(): ALKMaps;
};

export type MapUtilities = {
	zoomToCurrentLocation(): void;
	getCenterLonLatFromRegion(region: string): {zoom: number, lat: number, lon: number};
	LLtoMerc(lon: number, lat: number): LonLat;
};

export type AlkGeocodeService = {
	reverseGeocode(
		center: LonLat,
		options: {
			region: string;
			dataset: string;
		},
	): Promise<{ Address: Address }[] | null>;
};

export type Address = {
	StreetAddress: string;
	City: string;
	State: string;
	Zip: string;
	County: string | null;
	Country: string;
	StateName: string | null;
	StateAbbreviatione: string | null;
	CountryAbbreviation: string | null;
};

export type State = {
	abbreviation: string;
	name: string;
	country: string;
};
